import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { getHierarchyStructures } from '../api/hierarchy-structureAPI';
import { buildHierarchy } from '../utils/hierarchy';
import { formatDate } from '../utils/dateUtils';

const DEPARTMENT_PAGE_SIZE = 25;

const normaliseAssetResponse = (resp) => {
  if (!resp) {
    return { items: [], total: 0 };
  }

  if (Array.isArray(resp)) {
    return { items: resp, total: resp.length };
  }

  const items = Array.isArray(resp.data) ? resp.data : [];
  const total = typeof resp.total === 'number' ? resp.total : items.length;
  return { items, total };
};

const getTypeColour = (type) => {
  if (!type) return 'secondary';
  const t = type.toLowerCase();
  if (t === 'advanced' || t === 'standard') return 'primary';
  if (t === 'basic') return 'secondary';
  return 'secondary';
};

const getStatusColour = (status) => status?.colours?.bootstrapName || 'secondary';

const normaliseId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'object') {
    if (typeof value.id !== 'undefined') {
      return normaliseId(value.id);
    }
    if (typeof value.value !== 'undefined') {
      return normaliseId(value.value);
    }
  }

  if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      return null;
    }
    return String(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }

  return null;
};

const getAssetDepartmentId = (asset) => {
  if (!asset) {
    return null;
  }

  const fromId = normaliseId(asset.assetDepartmentId ?? asset.departmentId);
  if (fromId !== null) {
    return fromId;
  }

  if (asset.assetDepartment) {
    const nested = normaliseId(
      asset.assetDepartment.id ?? asset.assetDepartment.assetDepartmentId ?? asset.assetDepartment.value,
    );
    if (nested !== null) {
      return nested;
    }
  }

  return null;
};

const getAssetKey = (asset) => normaliseId(asset?.id) ?? normaliseId(asset?.assetNumber) ?? null;

const combineUniqueAssets = (existing = [], incoming = []) => {
  const seen = new Set();
  const result = [];

  const addUnique = (asset) => {
    if (!asset) {
      return;
    }
    const key = getAssetKey(asset) ?? JSON.stringify(asset);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(asset);
  };

  [...existing, ...incoming].forEach(addUnique);
  return result;
};

const isEmptyValue = (value) => {
  if (React.isValidElement(value)) {
    return false;
  }
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
};

const DetailItem = ({ label, value, skipIfEmpty = false, colSize = 6 }) => {
  if (skipIfEmpty && isEmptyValue(value)) {
    return null;
  }
  return (
    <div className={`col-md-${colSize}`}>
      <div className="text-muted fw-semibold mb-2">{label}</div>
      <div className="mt-1 mb-3">{isEmptyValue(value) ? '-' : value}</div>
    </div>
  );
};

const AssetPanelView = ({
  selectedAsset,
  setSelectedAsset,
  listContainerRef,
  onEdit,
  onDelete,
  planthireEnabled = false,
  canEdit = false,
  canDelete = false,
  fetchAssetsForDepartment,
  assetTreeRefreshKey = 0,
  searchTerm = '',
}) => {
  const [departmentRoots, setDepartmentRoots] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState(null);
  const [expandedDepartments, setExpandedDepartments] = useState([]);
  const [departmentAssets, setDepartmentAssets] = useState({});
  const expandedDepartmentsRef = useRef([]);

  const placeholder = useMemo(
    () => `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`,
    [],
  );

  useEffect(() => {
    let isActive = true;
    setDepartmentsLoading(true);
    (async () => {
      try {
        const response = await getHierarchyStructures();
        if (!isActive) return;
        const structures = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const options = structures
          .filter((item) => item && typeof item.id !== 'undefined')
          .map((item) => ({
            value: item.id,
            label: item.name,
            parentId: item.parentId ?? null,
            raw: item,
          }));
        const { roots } = buildHierarchy(options);
        setDepartmentRoots(roots);
        setDepartmentsError(null);
        setExpandedDepartments([]);
        setDepartmentAssets({});
      } catch (err) {
        if (!isActive) return;
        console.error('Error loading departments', err);
        setDepartmentRoots([]);
        setDepartmentsError('Unable to load departments');
      } finally {
        if (isActive) {
          setDepartmentsLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    expandedDepartmentsRef.current = expandedDepartments;
  }, [expandedDepartments]);

  const loadDepartmentAssets = useCallback(
    async (departmentId, page = 1, append = false) => {
      if (typeof fetchAssetsForDepartment !== 'function' || departmentId === null || departmentId === undefined) {
        return;
      }

      let shouldFetch = true;
      let previousItems = [];

      setDepartmentAssets((prev) => {
        const current = prev[departmentId] || {};
        if (current.loading) {
          shouldFetch = false;
          previousItems = current.items || [];
          return prev;
        }
        previousItems = current.items || [];
        return {
          ...prev,
          [departmentId]: {
            ...current,
            loading: true,
            error: null,
          },
        };
      });

      if (!shouldFetch) {
        return;
      }

      try {
        const response = await fetchAssetsForDepartment(departmentId, page, DEPARTMENT_PAGE_SIZE);
        const { items, total } = normaliseAssetResponse(response);
        const departmentIdValue = normaliseId(departmentId);
        const filteredItems = Array.isArray(items)
          ? items.filter((asset) => {
            const assetDepartmentId = getAssetDepartmentId(asset);
            return assetDepartmentId !== null && assetDepartmentId === departmentIdValue;
          })
          : [];

        const combinedItems = append
          ? combineUniqueAssets(previousItems, filteredItems)
          : combineUniqueAssets([], filteredItems);
        const totalCount = typeof total === 'number' ? total : undefined;
        const hasMore = typeof totalCount === 'number'
          ? page * DEPARTMENT_PAGE_SIZE < totalCount
          : (Array.isArray(items) ? items.length : 0) === DEPARTMENT_PAGE_SIZE;

        setDepartmentAssets((prev) => ({
          ...prev,
          [departmentId]: {
            items: combinedItems,
            loading: false,
            error: null,
            page,
            hasMore,
          },
        }));
      } catch (err) {
        console.error('Error loading assets for department', err);
        setDepartmentAssets((prev) => {
          const current = prev[departmentId] || {};
          return {
            ...prev,
            [departmentId]: {
              ...current,
              items: previousItems,
              loading: false,
              error: 'Unable to load assets',
            },
          };
        });
      }
    },
    [fetchAssetsForDepartment],
  );

  const handleToggleDepartment = useCallback((departmentId) => {
    setExpandedDepartments((prev) => {
      const isExpanded = prev.includes(departmentId);
      if (isExpanded) {
        return prev.filter((id) => id !== departmentId);
      }
      loadDepartmentAssets(departmentId, 1, false);
      return [...prev, departmentId];
    });
  }, [loadDepartmentAssets]);

  useEffect(() => {
    const expandedIds = expandedDepartmentsRef.current;
    if (!expandedIds.length) return;
    expandedIds.forEach((deptId) => {
      loadDepartmentAssets(deptId, 1, false);
    });
  }, [assetTreeRefreshKey, searchTerm, loadDepartmentAssets]);

  const handleSelect = (asset) => {
    if (typeof setSelectedAsset === 'function') {
      setSelectedAsset(asset);
    }
  };

  const handleDeleteAsset = useCallback(async () => {
    if (!selectedAsset?.id || typeof onDelete !== 'function') {
      return;
    }

    const targetId = normaliseId(selectedAsset.id);
    const targetAssetNumber = normaliseId(selectedAsset.assetNumber);

    let deleted = false;
    try {
      const result = await onDelete(selectedAsset.id);
      deleted = result !== false;
    } catch (err) {
      console.error('Error deleting asset', err);
      return;
    }

    if (!deleted) {
      return;
    }

    setDepartmentAssets((prev) => {
      let hasChanges = false;

      const updated = Object.entries(prev).reduce((acc, [deptId, data]) => {
        if (!data || !Array.isArray(data.items)) {
          acc[deptId] = data;
          return acc;
        }

        const shouldRemoveAsset = (asset) => {
          if (!asset) {
            return false;
          }

          const assetIdValue = normaliseId(asset.id);
          if (targetId !== null && assetIdValue !== null && assetIdValue === targetId) {
            return true;
          }

          const assetNumberValue = normaliseId(asset.assetNumber);
          if (
            targetAssetNumber !== null &&
            assetNumberValue !== null &&
            assetNumberValue === targetAssetNumber
          ) {
            return true;
          }

          return false;
        };

        const filteredItems = data.items.filter((asset) => !shouldRemoveAsset(asset));

        if (filteredItems.length !== data.items.length) {
          hasChanges = true;
          acc[deptId] = {
            ...data,
            items: filteredItems,
          };
        } else {
          acc[deptId] = data;
        }

        return acc;
      }, {});

      return hasChanges ? updated : prev;
    });
  }, [onDelete, selectedAsset]);

  const renderAssetNode = (asset) => {
    if (!asset) return null;
    const rowType = asset?.PlanningType?.typeDescription;
    const rowTypeColour = getTypeColour(rowType);
    const isSelected = selectedAsset?.id === asset.id;
    const statusColour = getStatusColour(asset?.plantStatus);

    return (
      <li key={`asset-${asset.id}`} className="list-group-item border-0 px-0 py-2">
        <div
          className="p-2 rounded-3"
          style={{
            cursor: 'pointer',
            backgroundColor: isSelected ? 'rgba(13, 110, 253, 0.1)' : 'transparent',
            border: isSelected ? '1px solid rgba(13, 110, 253, 0.35)' : '1px solid transparent',
          }}
          onClick={() => handleSelect(asset)}
        >
          <div className="d-flex flex-column gap-1">
            <div className="fw-semibold text-md text-break">
              <Icon
                icon="mage:delivery-truck"
                style={{ position: "relative", top: "-3px" }}
                className="text-lg me-8 text-primary"
              />
              {asset.assetNumber} {asset.assetDescription}
            </div>
          </div>
        </div>
      </li>
    );
  };

  const renderDepartmentNode = (node) => {
    if (!node) return null;

    const departmentId = node.id;
    const isExpanded = expandedDepartments.includes(departmentId);
    const state = departmentAssets[departmentId] || {};
    const assetsForDept = state.items || [];
    const isLoading = state.loading;
    const error = state.error;
    const hasMore = state.hasMore;
    const nextPage = (state.page || 1) + 1;
    const toggleIcon = isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right';

    return (
      <li key={`dept-${departmentId}`} className="list-group-item border-0 px-0 py-2">
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-link btn-sm px-1 py-0 text-decoration-none text-dark"
            onClick={() => handleToggleDepartment(departmentId)}
          >
            <Icon icon={toggleIcon} className="text-lg" />
          </button>
          <div className="flex-grow-1 d-flex align-items-center justify-content-between">
            <span className="fw-semibold text-md text-break">{node.option.label}</span>
            {assetsForDept.length > 0 && (
              <span className="badge bg-secondary-subtle text-secondary">
                {assetsForDept.length}
              </span>
            )}
          </div>
        </div>
        {isExpanded && (
          <ul className="list-group list-group-flush ms-3 ps-3 border-start">
            {node.children.map((child) => renderDepartmentNode(child))}
            {isLoading && (
              <li className="list-group-item border-0 px-0 py-2 text-md text-muted d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm" role="status" />
                Loading assets...
              </li>
            )}
            {!isLoading && error && (
              <li className="list-group-item border-0 px-0 py-2 text-danger">{error}</li>
            )}
            {!isLoading && !error && assetsForDept.length === 0 && node.children.length === 0 && (
              <li className="list-group-item border-0 px-0 py-2 text-md text-muted fst-italic">
                No assets found
              </li>
            )}
            {assetsForDept.map((asset) => renderAssetNode(asset))}
            {hasMore && (
              <li className="list-group-item border-0 px-0 py-2">
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0"
                  onClick={() => loadDepartmentAssets(departmentId, nextPage, true)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load more'}
                </button>
              </li>
            )}
          </ul>
        )}
      </li>
    );
  };

  const manufacturer =
    selectedAsset?.group?.manufacturer?.manufacturer ||
    '';
  const model =
    selectedAsset?.model ||
    selectedAsset?.assetModel ||
    selectedAsset?.group?.groupName ||
    '';
  const assetClass = selectedAsset?.group?.class?.className || '';
  const costCentre =
    selectedAsset?.costCenter?.costCenterDescription ||
    '';
  const project = selectedAsset?.project?.name || selectedAsset?.projectName || '';
  const customer = selectedAsset?.customer?.name || selectedAsset?.customerName || '';
  const planningType =
    selectedAsset?.PlanningType?.typeDescription ||
    '';
  const assetType =
    selectedAsset?.AssetType?.name ||
    '';
  const statusColour = getStatusColour(selectedAsset?.plantStatus);
  const typeColour = getTypeColour(planningType);

  const hireBadge = selectedAsset?.hireStatus !== undefined && selectedAsset?.hireStatus !== -1
    ? (
      <span
        className={`badge ${selectedAsset.hireStatus === 1
          ? 'bg-success-focus text-success'
          : 'bg-danger-focus text-danger'
          }`}
      >
        {selectedAsset.hireStatus === 1 ? 'On Hire' : 'Off Hire'}
      </span>
    )
    : null;

  return (
    <div className="row pb-16 pe-16" style={{ height: 'calc(100vh - 211px)' }}>
      <div className="col-md-4 h-100 d-flex flex-column ps-32">
        <div
          className="flex-grow-1 overflow-auto scroll"
          style={{ border: 'solid #eee 2px', borderRadius: '8px' }}
          ref={listContainerRef}
        >
          {departmentsLoading ? (
            <div className="text-center py-4">
              <span className="spinner-border spinner-border-sm" role="status" />
            </div>
          ) : departmentsError ? (
            <div className="text-danger p-3 text-sm">{departmentsError}</div>
          ) : departmentRoots.length === 0 ? (
            <div className="text-muted p-3 text-sm">No departments available</div>
          ) : (
            <ul className="list-group list-group-flush">
              {departmentRoots.map((node) => renderDepartmentNode(node))}
            </ul>
          )}
        </div>
      </div>
      <div className="col-md-8 h-100 d-flex flex-column">
        {selectedAsset ? (
          <div
            className="card flex-grow-1 d-flex flex-column overflow-hidden"
            style={{ border: 'solid #eee 2px', borderRadius: '8px' }}
          >
            <div className="card-header flex-shrink-0 card border-0">
              <div className="row align-items-center">
                <div className="col-md-8 d-flex align-items-start gap-3">
                  <div>
                    <h5 className="text-xl mb-2 d-flex align-items-center gap-2">
                      {selectedAsset.assetNumber} - {selectedAsset.assetDescription}
                    </h5>
                  </div>
                </div>
                <div className="col-md-4 d-flex justify-content-end align-items-start gap-2">
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm my-6"
                      onClick={() => onEdit && onEdit(selectedAsset)}
                    >
                      <Icon icon="mdi:pencil" className="text-xl me-6" style={{ position: 'relative', top: '-1px' }} />
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm my-6"
                      onClick={handleDeleteAsset}
                    >
                      <Icon icon="mdi:close" className="text-xl me-6" style={{ position: 'relative', top: '-1px' }} />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body flex-grow-1 overflow-auto scroll-sm">
              <div className="row g-4 mb-3">
                <div className="col-md-6">
                  <div
                    className="rounded-3 overflow-hidden"
                  >
                    <img
                      src={
                        selectedAsset.assetImage
                          ? `${process.env.REACT_APP_SPACES_BASE}/${selectedAsset.assetImage}`
                          : placeholder
                      }
                      alt="Asset"
                      className="object-fit- py-3"
                      style={{ height: "180px" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = placeholder;
                      }}
                    />
                  </div>
                </div>

              </div>

              <div className="row g-4">
                <DetailItem label="Description" value={selectedAsset.assetDescription || '-'} />
                <DetailItem label="Status" value={<span
                  className={`bg-${statusColour}-focus text-${statusColour}-main px-16 py-2 rounded-pill text-sm`}
                >
                  {selectedAsset.plantStatus.status}
                </span>} />
                <DetailItem
                  label="Hire Asset"
                  value={planthireEnabled ? (hireBadge || '-') : null}
                  skipIfEmpty={!planthireEnabled}
                />
                {planthireEnabled && (
                  <>
                    <DetailItem label="Customer" value={customer || '-'} />
                    <DetailItem label="Project" value={project || '-'} />
                  </>
                )}
                <DetailItem label="Manufacturer" value={manufacturer || '-'} skipIfEmpty />
                <DetailItem label="Model" value={model || '-'} skipIfEmpty />
                <DetailItem label="Class" value={assetClass || '-'} skipIfEmpty />
                <DetailItem label="Serial Number" value={selectedAsset.assetSerial || '-'} skipIfEmpty />
                <DetailItem label="Department" value={selectedAsset.assetDepartment?.name || '-'} />
                <DetailItem label="Cost Centre" value={costCentre || '-'} skipIfEmpty />
                <DetailItem
                  label="Start Date"
                  value={formatDate(selectedAsset.assetStartDate) || '-'}
                  skipIfEmpty
                />
                <DetailItem
                  label="End Date"
                  value={formatDate(selectedAsset.assetEndDate) || '-'}
                  skipIfEmpty
                />
                <DetailItem label="Warranty Period (Days)" value={selectedAsset.warrantyPeriodDays ?? '-'} skipIfEmpty />
                <DetailItem label="Build Year" value={selectedAsset.buildYear || '-'} skipIfEmpty />
                {selectedAsset.assetDisposed ? (
                  <DetailItem
                    label="Disposal Date"
                    value={formatDate(selectedAsset.assetDisposalDate) || '-'}
                    skipIfEmpty
                  />
                ) : ''}
                <DetailItem label="Planning Type" value={planningType ? <span className={`badge bg-${typeColour}`}>{planningType}</span> : '-'} />
                <DetailItem label="Asset Type" value={assetType ? <span className="badge bg-secondary">{assetType}</span> : '-'} />
                <DetailItem label="Notes" value={selectedAsset.notes || '-'} colSize={12} />
              </div>
            </div>
          </div>
        ) : (
          <div className="h-100 d-flex align-items-center justify-content-center text-muted">
            Select an asset to view details
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetPanelView;
